import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  Tooltip,
  Grid,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Cloud as CloudIcon,
  Public as PublicIcon,
  Lock as LockIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  FilterList as FilterIcon,
  Image as ImageIcon,
  PowerSettingsNew as PowerIcon
} from '@mui/icons-material';
import api, { Tenant } from '../services/api';
import { Provider, ProviderCreateRequest, ProviderUpdateRequest, SupportedProvider } from '../types/providers';
import { GlobalAdminOnly } from './PermissionGate';
import ProviderConfigForm from './ProviderConfigForm';
import RegionManagement from './RegionManagement';
import { useNotification } from '../contexts/NotificationContext';
import { useTimezone } from '../contexts/TimezoneContext';
import { formatUiDateOnly } from '../utils/dateUtils';
import { isProviderUiSupported, filterUiSupportedProviders } from '../utils/uiSupportedProviders';

const ProviderManagement: React.FC = () => {
  const { showNotification } = useNotification();
  const { timezone } = useTimezone();
  const [providers, setProviders] = useState<Provider[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [supportedProviders, setSupportedProviders] = useState<SupportedProvider[]>([]);
  const [credentials, setCredentials] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);
  // Dialog states
  const [providerDialogOpen, setProviderDialogOpen] = useState(false);
  const [regionTypes, setRegionTypes] = useState<Record<string, string>>({});
  const [machineValid, setMachineValid] = useState(true);
  const [saveError, setSaveError] = useState('');
  const [editingProvider, setEditingProvider] = useState<Provider | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [providerToDelete, setProviderToDelete] = useState<Provider | null>(null);
  const [cascadeDialogOpen, setCascadeDialogOpen] = useState(false);

  // Form states
  const [providerForm, setProviderForm] = useState<ProviderCreateRequest>({
    name: '',
    provider_type: 'aws',
    identifier: '',
    is_public: true,
    tenant_id: null,
    config: {},
    credentials: {}
  });

  // Filter states
  const [providerTypeFilter, setProviderTypeFilter] = useState<string>('');
  const [publicFilter, setPublicFilter] = useState<string>('');

  // Tab state
  const [activeTab, setActiveTab] = useState(0);
  const [selectedProviderId, setSelectedProviderId] = useState<string>('');

  const showSnackbarMessage = useCallback((message: string, severity: 'success' | 'error') => {
    showNotification(message, severity);
  }, [showNotification]);

  const loadProviders = useCallback(async () => {
    setLoading(true);
    try {
      console.log('Loading providers with filters:', { providerTypeFilter, publicFilter });
      const data = await api.getProviders(
        providerTypeFilter || undefined,
        publicFilter ? publicFilter === 'true' : undefined
      );
      console.log('Loaded providers:', data);
      setProviders(data);
    } catch (error: any) {
      showSnackbarMessage('Failed to load providers', 'error');
      console.error('Error loading providers:', error);
    } finally {
      setLoading(false);
    }
  }, [providerTypeFilter, publicFilter, showSnackbarMessage]);

  const loadTenants = useCallback(async () => {
    try {
      const data = await api.getTenants();
      setTenants(data);
    } catch (error: any) {
      console.error('Error loading tenants:', error);
    }
  }, []);

  const loadSupportedProviders = useCallback(async () => {
    try {
      const data = await api.getSupportedProviders();
      setSupportedProviders(data);
    } catch (error: any) {
      console.error('Error loading supported providers:', error);
    }
  }, []);

  useEffect(() => {
    loadSupportedProviders();
    loadProviders();
    loadTenants();
  }, [loadSupportedProviders, loadProviders, loadTenants]);

  const handleProviderDialogOpen = (provider?: Provider) => {
    setRegionTypes({}); setMachineValid(true); setSaveError('');
    if (provider) {
      setEditingProvider(provider);
      setProviderForm({
        name: provider.name,
        provider_type: provider.provider_type,
        identifier: provider.identifier,
        is_public: provider.is_public,
        tenant_id: provider.tenant_id,
        config: provider.config,
        credentials: {} // Credentials are never returned from API for security
      });
      setCredentials({}); // Reset credentials state
    } else {
      setEditingProvider(null);
      // Default to the first UI-supported provider type (falls back to 'aws' when
      // the supported list hasn't loaded yet or nothing is gated).
      const firstSupported = supportedProviders.find((sp) =>
        isProviderUiSupported(sp.provider_type)
      );
      const initialProviderType = (firstSupported?.provider_type ||
        'aws') as ProviderCreateRequest['provider_type'];
      const initializedConfig = initializeConfigWithRequiredFields(initialProviderType);
      setProviderForm({
        name: '',
        provider_type: initialProviderType,
        identifier: '',
        is_public: true,
        tenant_id: null,
        config: initializedConfig,
        credentials: {}
      });
      setCredentials({}); // Reset credentials state for new provider
    }
    setProviderDialogOpen(true);
  };

  const handleProviderDialogClose = () => {
    setProviderDialogOpen(false);
    setEditingProvider(null);
  };

  const validateProviderConfig = (config: Record<string, any>, providerType: string): string[] => {
    const supportedProvider = supportedProviders.find(sp => sp.provider_type === providerType);
    if (!supportedProvider?.config_schema) {
      return [];
    }

    const schema = supportedProvider.config_schema;
    const requiredFields = schema.required || [];
    const missingFields: string[] = [];

    // Check required fields
    requiredFields.forEach((fieldName: string) => {
      if (!(fieldName in config) || config[fieldName] === '' || config[fieldName] === null || config[fieldName] === undefined) {
        missingFields.push(fieldName);
      }
    });

    // Check required nested fields
    Object.entries(schema.properties || {}).forEach(([fieldName, fieldSchema]: [string, any]) => {
      if (fieldSchema.type === 'object' && fieldSchema.properties && config[fieldName]) {
        const nestedRequired = fieldSchema.required || [];
        nestedRequired.forEach((nestedFieldName: string) => {
          if (!(nestedFieldName in config[fieldName]) || config[fieldName][nestedFieldName] === '' || config[fieldName][nestedFieldName] === null || config[fieldName][nestedFieldName] === undefined) {
            missingFields.push(`${fieldName}.${nestedFieldName}`);
          }
        });
      }
    });

    return missingFields;
  };

  // Keep credentials repair and unrelated edits possible when AWS discovery is down.
  // Every machine edit still needs a valid catalog and capacity before it can save.
  const machineSettingsChanged = !editingProvider || Object.keys(regionTypes).length > 0 ||
    ['compute', 'bot_runtime', 'container_role_arn', 'container_fleet'].some(key =>
      JSON.stringify(providerForm.config[key]) !== JSON.stringify(editingProvider.config[key]));

  const handleProviderSubmit = async () => {
    try {
      // Validate that the selected provider type is supported
      const isSupported = supportedProviders.some(sp => sp.provider_type === providerForm.provider_type);
      if (!isSupported) {
        showSnackbarMessage('Selected provider type is not supported', 'error');
        return;
      }

      // Validate required fields
      const missingFields = validateProviderConfig(providerForm.config, providerForm.provider_type);
      if (missingFields.length > 0) {
        showSnackbarMessage(`Missing required fields: ${missingFields.join(', ')}`, 'error');
        return;
      }

      if (editingProvider) {
        const updateData: ProviderUpdateRequest = {
          name: providerForm.name,
          status: editingProvider.status,
          identifier: providerForm.identifier,
          is_public: providerForm.is_public,
          tenant_id: providerForm.is_public ? null : providerForm.tenant_id || null,
          config: providerForm.config,
          ...(providerForm.provider_type === 'aws' ? { region_instance_types: regionTypes } : {}),
          credentials: Object.keys(credentials).length > 0 ? credentials : undefined
        };
        await api.updateProvider(editingProvider.id, updateData);
        showSnackbarMessage('Provider updated successfully', 'success');
      } else {
        await api.createProvider({
          ...providerForm,
          credentials
        });
        showSnackbarMessage('Provider created successfully', 'success');
      }
      handleProviderDialogClose();
      loadProviders();
    } catch (error: any) {
      setSaveError(error.response?.data?.detail || 'Failed to save provider');
      showSnackbarMessage(error.response?.data?.detail || 'Failed to save provider', 'error');
    }
  };

  const handleToggleProviderStatus = async (provider: Provider, e: React.MouseEvent) => {
    e.stopPropagation();
    const newStatus = provider.status === 'active' ? 'inactive' : 'active';
    try {
      await api.updateProvider(provider.id, { status: newStatus });
      showSnackbarMessage(
        `Provider "${provider.name}" ${newStatus === 'active' ? 'enabled' : 'disabled'}`,
        'success'
      );
      loadProviders();
    } catch (error: any) {
      showSnackbarMessage(error.response?.data?.detail || 'Failed to update provider status', 'error');
    }
  };

  const handleDeleteProvider = (provider: Provider) => {
    setProviderToDelete(provider);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteProvider = async () => {
    if (!providerToDelete) return;
    
    try {
      await api.deleteProvider(providerToDelete.id);
      showSnackbarMessage('Provider deleted successfully', 'success');
      setDeleteDialogOpen(false);
      setProviderToDelete(null);
      loadProviders();
    } catch (error: any) {
      const detail: string | undefined = error?.response?.data?.detail;
      if (detail && /associated regions/i.test(detail)) {
        // Offer cascade deletion if regions exist
        setDeleteDialogOpen(false);
        setCascadeDialogOpen(true);
      } else {
        showSnackbarMessage(detail || 'Failed to delete provider', 'error');
      }
    }
  };

  const confirmCascadeDeleteProvider = async () => {
    if (!providerToDelete) return;
    try {
      await api.deleteProvider(providerToDelete.id, { cascade: true });
      showSnackbarMessage('Provider and all regions deleted', 'success');
      setCascadeDialogOpen(false);
      setProviderToDelete(null);
      loadProviders();
    } catch (error: any) {
      const detail: string | undefined = error?.response?.data?.detail;
      showSnackbarMessage(detail || 'Failed to cascade delete provider', 'error');
    }
  };

  const getProviderColor = (providerType: string): 'primary' | 'success' | 'warning' | 'secondary' => {
    switch (providerType) {
      case 'aws': return 'primary';
      case 'gcp': return 'success';
      case 'azure': return 'warning';
      case 'openstack': return 'secondary';
      default: return 'primary';
    }
  };

  const getProviderIcon = (providerType: string) => {
    return <CloudIcon />;
  };

  const getProviderDisplayName = (providerType: string): string => {
    const supportedProvider = supportedProviders.find(sp => sp.provider_type === providerType);
    return supportedProvider?.display_name || providerType.toUpperCase();
  };

  const initializeConfigWithRequiredFields = (providerType: string, currentConfig: Record<string, any> = {}): Record<string, any> => {
    const supportedProvider = supportedProviders.find(sp => sp.provider_type === providerType);
    if (!supportedProvider?.config_schema?.properties) {
      return currentConfig;
    }

    const newConfig = { ...currentConfig };
    const schema = supportedProvider.config_schema.properties;

    // Recursively initialize required fields
    const initializeFields = (schemaSection: Record<string, any>, configSection: Record<string, any> = {}) => {
      const result = { ...configSection };
      
      for (const [key, fieldSchema] of Object.entries(schemaSection)) {
        if (fieldSchema.required && !(key in result)) {
          // Set default values based on field type
          if (fieldSchema.type === 'string') {
            result[key] = '';
          } else if (fieldSchema.type === 'number') {
            result[key] = 0;
          } else if (fieldSchema.type === 'boolean') {
            result[key] = false;
          } else if (fieldSchema.type === 'array') {
            result[key] = [];
          } else if (fieldSchema.type === 'object') {
            result[key] = {};
          }
        }
        
        // Recursively handle nested objects
        if (fieldSchema.type === 'object' && fieldSchema.properties) {
          result[key] = initializeFields(fieldSchema.properties, result[key] || {});
        }
      }
      
      return result;
    };

    return initializeFields(schema, newConfig);
  };

  // UI-only gating: provider types offered in the type filter and the create form.
  // Disabled types stay hidden everywhere they could be selected/created.
  const uiSupportedProviders = filterUiSupportedProviders(
    supportedProviders,
    (sp) => sp.provider_type
  );
  // Hide already-configured providers whose type is UI-disabled from the table.
  const visibleProviders = filterUiSupportedProviders(
    providers,
    (p) => p.provider_type
  );
  // In the create/edit dialog, keep the edited provider's own type visible even if
  // it has since been disabled, so its config can still be viewed/updated.
  const providerTypeOptions =
    editingProvider &&
    !uiSupportedProviders.some((sp) => sp.provider_type === editingProvider.provider_type)
      ? [
          ...uiSupportedProviders,
          ...supportedProviders.filter(
            (sp) => sp.provider_type === editingProvider.provider_type
          ),
        ]
      : uiSupportedProviders;

  return (
    <GlobalAdminOnly fallback={
      <Alert severity="error">
        You do not have permission to manage providers. Only super administrators can manage providers.
      </Alert>
    }>
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1">
          Provider Management
        </Typography>
        {activeTab === 0 && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleProviderDialogOpen()}
          >
            Add Provider
          </Button>
        )}
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab 
            label="Providers" 
            icon={<CloudIcon />} 
            iconPosition="start"
          />
          <Tab 
            label="Regions" 
            icon={<ImageIcon />} 
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {activeTab === 0 && (
        <>
          {/* Filters */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <FilterIcon />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Provider Type</InputLabel>
                <Select
                  value={providerTypeFilter}
                  label="Provider Type"
                  onChange={(e) => setProviderTypeFilter(e.target.value)}
                >
                  <MenuItem value="">All Types</MenuItem>
                  {uiSupportedProviders.map((supportedProvider) => (
                    <MenuItem key={supportedProvider.provider_type} value={supportedProvider.provider_type}>
                      {supportedProvider.display_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Visibility</InputLabel>
                <Select
                  value={publicFilter}
                  label="Visibility"
                  onChange={(e) => setPublicFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Public</MenuItem>
                  <MenuItem value="false">Private</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Paper>

          {/* Providers Table */}
          <Paper>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6">Cloud Providers</Typography>
            </Box>
            {loading && <LinearProgress />}
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Identifier</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Visibility</TableCell>
                    <TableCell>Tenant</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleProviders.map((provider) => (
                    <TableRow 
                      key={provider.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        setSelectedProviderId(provider.id);
                        setActiveTab(1); // Switch to Regions tab
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getProviderIcon(provider.provider_type)}
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {provider.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getProviderDisplayName(provider.provider_type)}
                          color={getProviderColor(provider.provider_type)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {provider.identifier}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {provider.status === 'active' ? (
                            <CheckCircleIcon color="success" fontSize="small" />
                          ) : (
                            <CancelIcon color="error" fontSize="small" />
                          )}
                          <Chip
                            label={provider.status}
                            color={provider.status === 'active' ? 'success' : 'error'}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {provider.is_public ? (
                            <PublicIcon color="primary" fontSize="small" />
                          ) : (
                            <LockIcon color="action" fontSize="small" />
                          )}
                          <Typography variant="body2">
                            {provider.is_public ? 'Public' : 'Private'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {provider.tenant_id ? 
                            tenants.find(t => t.id === provider.tenant_id)?.name || 'Unknown' : 
                            'N/A'
                          }
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {formatUiDateOnly(provider.created_at, timezone)}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title={provider.status === 'active' ? 'Disable provider' : 'Enable provider'}>
                            <IconButton
                              size="small"
                              onClick={(e) => handleToggleProviderStatus(provider, e)}
                              color={provider.status === 'active' ? 'success' : 'default'}
                            >
                              <PowerIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleProviderDialogOpen(provider);
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Manage Images">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedProviderId(provider.id);
                                setActiveTab(1);
                              }}
                            >
                              <ImageIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteProvider(provider);
                              }}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      {activeTab === 1 && (
        <RegionManagement selectedProviderId={selectedProviderId} />
      )}

      {/* Create/Edit Provider Dialog */}
      <Dialog open={providerDialogOpen} onClose={handleProviderDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingProvider ? 'Edit Provider' : 'Create Provider'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name"
                value={providerForm.name}
                onChange={(e) => setProviderForm({ ...providerForm, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Provider Type</InputLabel>
                <Select
                  value={providerForm.provider_type}
                  label="Provider Type"
                  onChange={(e) => {
                    const newProviderType = e.target.value as any;
                    const initializedConfig = initializeConfigWithRequiredFields(newProviderType, providerForm.config);
                    setProviderForm({ 
                      ...providerForm, 
                      provider_type: newProviderType,
                      config: initializedConfig
                    });
                  }}
                >
                  {providerTypeOptions.map((supportedProvider) => (
                    <MenuItem key={supportedProvider.provider_type} value={supportedProvider.provider_type}>
                      {supportedProvider.display_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Identifier"
                value={providerForm.identifier}
                onChange={(e) => setProviderForm({ ...providerForm, identifier: e.target.value })}
                required
                helperText="Unique identifier for this provider"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={providerForm.is_public}
                    onChange={(e) => setProviderForm({ ...providerForm, is_public: e.target.checked })}
                  />
                }
                label="Public Provider (available to all users)"
              />
            </Grid>
            {!providerForm.is_public && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Tenant</InputLabel>
                  <Select
                    value={providerForm.tenant_id || ''}
                    label="Tenant"
                    onChange={(e) => setProviderForm({ ...providerForm, tenant_id: e.target.value || null })}
                  >
                    <MenuItem value="">Select Tenant</MenuItem>
                    {tenants.map((tenant) => (
                      <MenuItem key={tenant.id} value={tenant.id}>
                        {tenant.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
              <ProviderConfigForm
                providerId={editingProvider?.id}
                regionTypes={regionTypes} onRegionTypesChange={setRegionTypes} onValidityChange={setMachineValid}
                config={providerForm.config}
                onChange={(config) => setProviderForm({ ...providerForm, config })}
                credentials={credentials}
                onCredentialsChange={setCredentials}
                providerType={providerForm.provider_type}
                configSchema={supportedProviders.find(sp => sp.provider_type === providerForm.provider_type)?.config_schema}
                credentialsSchema={supportedProviders.find(sp => sp.provider_type === providerForm.provider_type)?.credentials_schema}
                disabled={false}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleProviderDialogClose}>Cancel</Button>
          <Button onClick={handleProviderSubmit} variant="contained" disabled={providerForm.provider_type === 'aws' && machineSettingsChanged && !machineValid}>
            {editingProvider ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Provider</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the provider "{providerToDelete?.name}"? 
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDeleteProvider} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cascade Delete Confirmation Dialog */}
      <Dialog open={cascadeDialogOpen} onClose={() => setCascadeDialogOpen(false)}>
        <DialogTitle>Delete Provider and Regions</DialogTitle>
        <DialogContent>
          <Typography>
            This provider has regions configured. Do you want to delete all regions
            belonging to "{providerToDelete?.name}" and then delete the provider?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Note: Any region currently used by bots will block deletion. You'll receive
            an error listing blocking regions if present.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCascadeDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmCascadeDeleteProvider} color="error" variant="contained">
            Delete Provider and Regions
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
    </GlobalAdminOnly>
  );
};

export default ProviderManagement;
